import { cn } from "@/shared/utils/core";
import { IconButton } from "@/shared/components/ui/icon-button";
import { ArrowLeft, ArrowRight } from "lucide-react";

// Reusable Table Component
export interface TableColumn<T> {
  header: string | React.ReactNode;
  accessor: keyof T | ((item: T, index: number) => React.ReactNode);
  className?: string;
  align?: "left" | "center" | "right";
  thProps?: React.ThHTMLAttributes<HTMLTableCellElement>;
}

export interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  maxHeight?: string;
  className?: string;
  tableHeaderClassName?: string;
  action?: React.ReactNode;
  pagination?: TablePaginationProps;
  onRowClick?: (item: T, index: number) => void;
}

export default function Table<T>({
  columns,
  data,
  maxHeight = "350px",
  className,
  tableHeaderClassName,
  action,
  pagination,
  onRowClick,
}: TableProps<T>) {
  return (
    <div className={cn(`overflow-auto max-h-[${maxHeight}]`, className)}>
      <table className="w-full">
        <thead className={cn("sticky top-0", tableHeaderClassName)}>
          <tr className="">
            {columns.map((column, index) => (
              <th
                key={index}
                {...column.thProps}
                className={cn(
                  "px-6 py-3 text-stone-600 font-normal",
                  {
                    "text-left": !column.align || column.align === "left",
                    "text-center": column.align === "center",
                    "text-right": column.align === "right",
                  },
                  column.className,
                  column.thProps?.className,
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((item, rowIndex) => (
            <tr
              key={rowIndex}
              onClick={onRowClick ? () => onRowClick(item, rowIndex) : undefined}
              className={onRowClick ? "cursor-pointer hover:bg-subtle" : undefined}
            >
              {columns.map((column, colIndex) => (
                <td
                  key={colIndex}
                  className={cn(
                    "px-6 py-4 whitespace-nowrap",
                    {
                      "text-left": !column.align || column.align === "center",
                      "text-center": column.align === "center",
                      "text-right": column.align === "right",
                    },
                    column.className,
                  )}
                >
                  {typeof column.accessor === "function"
                    ? column.accessor(item, rowIndex)
                    : (item[column.accessor] as React.ReactNode)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {(action || pagination) && (
        <div className="border-t w-full flex justify-between">
          <div className="flex justify-start">{action}</div>
          {pagination && (
            <div className="flex justify-end p-3 self-end">
              <div className="flex items-center gap-2">
                <p className="text-sm mb-0">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </p>
                <IconButton
                  onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  label="Previous page"
                >
                  <ArrowLeft />
                </IconButton>
                <IconButton
                  onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage >= pagination.totalPages}
                  label="Next page"
                >
                  <ArrowRight />
                </IconButton>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
